/**
 * AppSpecialEditorDialogs — CC-3 special-editor dialog MOUNT.
 *
 * The dedicated dialogs-mount for the shared `SpecialEditorHost`, following the
 * `AppStyleEditorDialogs` pattern: App.tsx (or the future DIALOG-HOST module —
 * §5, "естественный шов — расширение CC-3 SpecialEditorHost") calls
 * `useSpecialEditorHost()` and renders `<AppSpecialEditorDialogs editor={…} />`
 * instead of inlining the mount in the App.tsx God-file. This component is pure
 * (calls no hooks) — ownership of open/close state stays with the hook.
 *
 * The per-entity FORMS are OUT OF SCOPE of CC-3 and land in downstream slices
 * (REVCLOUD-PROPS-EDITOR / HATCH-EDIT-DIALOG / ARRAY-SPEC-EDITOR). Each registers
 * its `{ titleKey, form }` in `SPECIAL_EDITOR_REGISTRY` below — the ONE extension
 * point. The CC-3 scaffold ships that registry EMPTY, so with no form registered
 * this component renders nothing (no placeholder form — ПРИОРИТЕТ #1). The shared
 * host + write-back → regen plumbing is fully real and exercised by any injected
 * registry (`registry` prop / tests).
 */

import type { ReactElement } from "react";

import type { EntityType } from "@dxf-ai-editor/contracts";

import type { MessageKey } from "../i18n";
import { useSpecialEditorHost } from "../hooks/useSpecialEditorHost";
import type { UseSpecialEditorHostResult } from "../hooks/useSpecialEditorHost";
import {
  SpecialEditorHost,
  type SpecialEditorFormComponent,
} from "./SpecialEditorHost";
import { RevcloudPropsEditorForm } from "./RevcloudPropsEditorForm";
import { HatchEditForm } from "./HatchEditForm";

/** One entity kind's special-editor registration — the title + the injected
 *  per-entity form. Downstream form slices add their entry here. */
export interface SpecialEditorRegistration {
  /** i18n key for the dialog title (RU+EN via `messages.ts`). */
  titleKey: MessageKey;
  /** the per-entity form (separate slice). */
  form: SpecialEditorFormComponent;
  /** optional i18n key for the regen telemetry summary. */
  regenSummaryKey?: MessageKey;
}

/**
 * The special-editor form registry — the ONE extension point. Each per-entity
 * form slice registers its kind here and nothing else in the host / mount
 * changes.
 *
 * REVCLOUD-PROPS-EDITOR (fullstack §5.1) — the FIRST real form: the revision-cloud
 * arc/style/mode editor (`REVCLOUDPROPERTIES` / `ОБЛАКОСВ`). It reads the DEFUSE
 * S15 typed descriptors and writes back through `properties/apply`, which the host
 * dispatches + regens (the render adapter re-tessellates the cloud from the new
 * arc length).
 *
 * HATCH-EDIT-DIALOG (fullstack §5.1) — the SECOND real form: the "Hatch Edit"
 * dialog (`HATCHEDIT` / `РЕДШТРИХ`). It edits the hatch pattern type / name /
 * angle / scale / associativity / island-detection style through the same typed
 * descriptors + `properties/apply` safe contour; the host regens (the render
 * adapter re-tiles the fill from the new pattern props).
 */
export const SPECIAL_EDITOR_REGISTRY: Partial<
  Record<EntityType, SpecialEditorRegistration>
> = {
  "Revision Cloud": {
    titleKey: "special_editor.revcloud.title",
    form: RevcloudPropsEditorForm,
    regenSummaryKey: "special_editor.revcloud.regen_summary",
  },
  Hatch: {
    titleKey: "special_editor.hatch.title",
    form: HatchEditForm,
    regenSummaryKey: "special_editor.hatch.regen_summary",
  },
};

export interface AppSpecialEditorDialogsProps {
  /** the open/close controller result from `useSpecialEditorHost`. */
  editor: UseSpecialEditorHostResult;
  /** override the form registry — used by a composing form slice or a test to
   *  inject its per-entity form. Defaults to the (empty) module registry. */
  registry?: Partial<Record<EntityType, SpecialEditorRegistration>>;
}

export function AppSpecialEditorDialogs({
  editor,
  registry = SPECIAL_EDITOR_REGISTRY,
}: AppSpecialEditorDialogsProps): ReactElement | null {
  const { entity, close } = editor;
  if (!entity) return null;

  const registration = registry[entity.type];
  // No form registered for this kind — the CC-3 scaffold ships no forms, so
  // this is the inert default. A downstream slice fills the registry.
  if (!registration) return null;

  return (
    <SpecialEditorHost
      titleKey={registration.titleKey}
      entity={entity}
      form={registration.form}
      onClose={close}
      {...(registration.regenSummaryKey
        ? { regenSummaryKey: registration.regenSummaryKey }
        : {})}
    />
  );
}

// Re-export the controller hook so the mount + its controller share one import
// site for the downstream DIALOG-HOST / form slices (mirrors the style-editor
// dialogs bundling their hook result type through the mount module).
export { useSpecialEditorHost };
export type { UseSpecialEditorHostResult };
